import React from 'react';
import { Play } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type {
  ChangeGranularity,
  DRBFMInputPattern,
  DRBFMWorkflowPhase,
} from '../../../types/drbfm';

export interface WorkflowStartValue {
  workflowPhase: DRBFMWorkflowPhase;
  inputPattern: DRBFMInputPattern;
  changeGranularity: ChangeGranularity;
}

interface DRBFMWorkflowStartStepProps {
  value: WorkflowStartValue;
  isLoading: boolean;
  onChange: (value: WorkflowStartValue) => void;
  onStart: () => void | Promise<void>;
}

const phaseOptions: Array<{ value: DRBFMWorkflowPhase; labelKey: string; label: string }> = [
  { value: 'requirements_change', labelKey: 'drbfm.workflow.input.phaseRequirementsChange', label: '要求変更' },
  { value: 'concept_estimate', labelKey: 'drbfm.workflow.input.phaseConceptEstimate', label: '構想・見積' },
  { value: 'detailed_design', labelKey: 'drbfm.workflow.input.phaseDetailedDesign', label: '詳細設計' },
  { value: 'fmea_drbfm', labelKey: 'drbfm.workflow.input.phaseFmeaDrbfm', label: 'FMEA/DRBFM' },
  { value: 'post_process_check', labelKey: 'drbfm.workflow.input.phasePostProcessCheck', label: '後工程確認' },
];

const inputPatternOptions: Array<{ value: DRBFMInputPattern; labelKey: string; label: string }> = [
  { value: 'function_part_matrix', labelKey: 'drbfm.workflow.input.inputPatternFunctionPartMatrix', label: '機能一覧 × 部品ツリー' },
  { value: 'old_new_documents', labelKey: 'drbfm.workflow.input.inputPatternOldNewDocuments', label: '旧新ドキュメント' },
  { value: 'change_report', labelKey: 'drbfm.workflow.input.inputPatternChangeReport', label: '変化点資料' },
  { value: 'old_new_cad', labelKey: 'drbfm.workflow.input.inputPatternOldNewCad', label: '旧新CAD' },
  { value: 'old_new_drawings', labelKey: 'drbfm.workflow.input.inputPatternOldNewDrawings', label: '旧新図面' },
];

const granularityOptions: Array<{ value: ChangeGranularity; labelKey: string; label: string }> = [
  { value: 'coarse', labelKey: 'drbfm.workflow.input.granularityCoarse', label: '粗い粒度' },
  { value: 'fine', labelKey: 'drbfm.workflow.input.granularityFine', label: '細かい粒度' },
  { value: 'mixed', labelKey: 'drbfm.workflow.input.granularityMixed', label: '混在' },
];

const DRBFMWorkflowStartStep: React.FC<DRBFMWorkflowStartStepProps> = ({
  value,
  isLoading,
  onChange,
  onStart,
}) => {
  const { t } = useTranslation();

  const updateValue = (patch: Partial<WorkflowStartValue>) => {
    onChange({ ...value, ...patch });
  };

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="revy-cjk text-base font-medium leading-[1.6]">
          {t('drbfm.workflow.startTitle', '工程別ワークフロー')}
        </h2>
        <p className="revy-cjk text-sm leading-[1.6] text-muted-foreground">
          {t('drbfm.workflow.startDescription', '工程、入力、粒度を選んで中間成果物を保存しながら進めます。')}
        </p>
      </div>

      <div className="flex flex-col gap-4 min-[900px]:flex-row">
        <Field label={t('drbfm.workflow.input.phaseFieldLabel', '工程フェーズ')} className="min-w-0 flex-1">
          {(control) => (
            <Select
              value={value.workflowPhase}
              disabled={isLoading}
              onValueChange={(next) => updateValue({ workflowPhase: next as DRBFMWorkflowPhase })}
            >
              <SelectTrigger {...control} className="revy-cjk">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="revy-ui">
                {phaseOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value} className="revy-cjk">
                    {t(option.labelKey, option.label)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Field>

        <Field label={t('drbfm.workflow.input.inputPatternFieldLabel', '入力パターン')} className="min-w-0 flex-1">
          {(control) => (
            <Select
              value={value.inputPattern}
              disabled={isLoading}
              onValueChange={(next) => updateValue({ inputPattern: next as DRBFMInputPattern })}
            >
              <SelectTrigger {...control} className="revy-cjk">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="revy-ui">
                {inputPatternOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value} className="revy-cjk">
                    {t(option.labelKey, option.label)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Field>

        <Field label={t('drbfm.workflow.input.changeGranularityFieldLabel', '変更粒度')} className="min-w-0 flex-1">
          {(control) => (
            <Select
              value={value.changeGranularity}
              disabled={isLoading}
              onValueChange={(next) => updateValue({ changeGranularity: next as ChangeGranularity })}
            >
              <SelectTrigger {...control} className="revy-cjk">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="revy-ui">
                {granularityOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value} className="revy-cjk">
                    {t(option.labelKey, option.label)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Field>
      </div>

      <div className="flex justify-end">
        <Button disabled={isLoading} onClick={onStart}>
          <Play strokeWidth={1.9} />
          <span className="revy-cjk">{t('drbfm.workflow.input.startProcess', '工程を開始')}</span>
        </Button>
      </div>
    </div>
  );
};

export default DRBFMWorkflowStartStep;
